import { ViewShell } from "@shared/components/menus/views/ViewShell"
import { Button } from "@shared/components/untitledui/base/buttons/button"
import CommentTiptap from "@shared/unified-tiptap/CommentTiptap"
import type { Editor } from "@tiptap/core"
import { useMemo, useRef, useState } from "react"
import {
  commentIsEmpty,
  readCommentHtml,
} from "../../extensions/comment-content/comment-content-extension"
import { commentDrafts } from "./comment-draft"

/**
 * Writing a new card comment, in the shape it will take on the card rather than a form
 * standing in for it. Nothing is written until Add: the caller stores these words.
 */
export function AddCommentView({
  draftKey,
  onCancel,
  onSubmit,
}: {
  /** Names the draft this view resumes and writes to — see {@link commentDrafts}. */
  draftKey: string | null
  onCancel: () => void
  /** Called with the composed comment as HTML. */
  onSubmit: (commentText: string) => void
}) {
  const editorRef = useRef<Editor | null>(null)
  const draft = useMemo(() => (draftKey === null ? "" : commentDrafts.read(draftKey)), [draftKey])
  const [isEmpty, setIsEmpty] = useState(draft === "")

  const submit = () => {
    const editor = editorRef.current
    if (!editor || commentIsEmpty(editor.state)) return
    if (draftKey !== null) commentDrafts.clear(draftKey)
    onSubmit(readCommentHtml(editor.state))
  }

  return (
    <ViewShell
      actions={
        <>
          <Button color="secondary" size="md" className="flex-1" onPress={onCancel}>
            Cancel
          </Button>
          <Button
            data-test="view confirm"
            color="primary"
            size="md"
            className="flex-1"
            isDisabled={isEmpty}
            onPress={submit}
          >
            Add
          </Button>
        </>
      }
    >
      <div className="px-5 pt-4">
        <CommentTiptap
          editorRef={editorRef}
          initialHtml={draft}
          autofocus="end"
          ariaLabel="Comment"
          onUpdate={(editor) => {
            setIsEmpty(commentIsEmpty(editor.state))
            // Every keystroke, so the words survive an Escape or a press outside the menu.
            if (draftKey !== null) commentDrafts.save(draftKey, readCommentHtml(editor.state))
          }}
          onSubmitShortcut={submit}
          className="min-h-20 rounded-md border border-border-secondary px-2.5 py-1.5 text-sm focus-within:border-brand"
        />
      </div>
    </ViewShell>
  )
}
